import { collection, doc, getDocs, onSnapshot, query, setDoc, where } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { db } from "../../../firebase";

import { Dashboard } from "../dashboard/dashboard";
import Navbar from "../global/navbar/navbar";
import Sidebar from "../global/sidebar/sidebar";
import { OrganizationsPage } from "../organizations/OrganizationsPage";
import { POS } from "../pos/POS";
import { History } from "../history/History";
import TicketingOverview from "../ticketing/ticketingOverview/TicketingOverview";
import TicketingDashboard from "../ticketing/ticketingDashboard/TicketingDashboard";
import TicketingCreateEdit from "../ticketing/ticketingCreateEdit/TicketingCreateEdit";
import TakeawayOverview from "../takeaway/takeawayOverview/TakeawayOverview";
import TakeawayUpdateInscription from "../takeaway/takeawayUpdateInscription/TakeawayUpdateInscription";
import TablePlan from "../tablePlan/TablePlan";
import CashlessPage from "../cashless/CashlessPage";
import BartrackOverview from "../bartrack/bartrackOverview/BartrackOverview";
import BartrackLineDetails from "../bartrack/bartrackLineDetails/BartrackLineDetails";
import BartrackSetup from "../bartrack/bartrackSetup/BartrackSetup";
import { Loader } from "../../../helper/loader";
import { useNavigate } from "react-router-dom";
import StockOverview from "../stockManagement/StockOverview";
import Marketplace from "../marketplace/mainPage/Providers";
import MarketplaceCart from "../marketplace/cart/MarketplaceCart";
import ProviderInfo from "../marketplace/provider/ProviderInfo";
import ENV from "../../../env";
import BudgetDashboard from "../budget/BudgetDashboard";
import Accreditations from "../accreditations/Accreditations";
import NotesPage from "../notes/NotesPage";
import DashboardAppSelectorModal from "../../../components/dashboardAppSelector/DashboardAppSelectorModal";
import userAction from "../../../redux/users/action";
import { todayRange } from "../../../utility/posRevenueComparison";

function DashboardRouter() {
  const { next, providerId } = useParams();
  const userStore = useSelector((state) => state.userReducer);
  const dispatch = useDispatch();
  const { setUser } = userAction;
  const location = useLocation();
  const [dateRange, setDateRange] = useState(todayRange);
  const [startDate, endDate] = dateRange;
  const [dateClickHandle, setDateClickHandle] = useState(false);
  const [dateResetHandle, setDateResetHandle] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [comparisonConfiguration, setComparisonConfiguration] = useState({ secondPeriod: null, applied: null, opened: false });
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState({});
  const [orgId, setOrgId] = useState([]);
  const [isCreatingNewPos, setIsCreatingNewPos] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isAppSelectorOpen, setIsAppSelectorOpen] = useState(false);
  const [appSelectorMode, setAppSelectorMode] = useState("settings");
  const [isSavingDashboardApps, setIsSavingDashboardApps] = useState(false);
  const navigate = useNavigate();

  const selectOrgId = (nextOrgId) => {
    setIsCreatingNewPos(false);
    setOrgId(nextOrgId);
  };

  const startNewPosCreation = () => {
    setIsCreatingNewPos(true);
    navigate("/dashboard/organizations");
    setOrgId([]);
  };

  useEffect(() => {
    if (userStore.users?.uid) {
      getOrganizations();
    }
  }, [userStore.users]);

  useEffect(() => {
    if (!userStore.users?.uid) return;
    if (userStore.users?.dashboardApps?.preferencesConfirmedAt) return;

    setAppSelectorMode("onboarding");
    setIsAppSelectorOpen(true);
  }, [userStore.users?.uid, userStore.users?.dashboardApps?.preferencesConfirmedAt]);

  useEffect(() => {
    setOrganization(organizations.find((org) => org.id === orgId[0]));
  }, [orgId, organizations]);

  useEffect(() => {
    if (next === "ticketing" && location.state?.selectedOrgId && organizations.length > 0) {
      const targetOrgId = location.state.selectedOrgId;
      if (orgId[0] !== targetOrgId) {
        selectOrgId([targetOrgId]);
      }
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [next, location.state, organizations]);

  useEffect(() => {
    if (!userStore.users?.uid) return;

    const q = query(
      collection(db, "Organizations"),
      where("adminUid", "array-contains", userStore.users.uid),
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      // Some legacy organisation documents do not persist their own `id`
      // field. Always retain the Firestore document id so a snapshot refresh
      // (for example after saving a promo code) cannot make the current POS
      // look absent and fall back to the first organisation.
      const records = querySnapshot.docs.map((organizationDoc) => {
        const data = organizationDoc.data();
        return { ...data, id: data.id || organizationDoc.id };
      });
      setOrganizations(records);
      setOrgId((prev) => {
        if (next === "organizations" && isCreatingNewPos) return [];
        const index = records.findIndex((record) => record.id === prev[0]);
        if (index !== -1) return prev;
        return records.length > 0 ? [records[0].id] : [];
      });
    });

    return () => unsubscribe();
  }, [userStore.users?.uid, next, isCreatingNewPos]);

  // The event's stored organization is authoritative, including on a reload
  // where the sidebar may initially select the first available organization.
  const resolveTicketingOrganization = (eventOrgId) => {
    const target = organizations.find((item) => String(item.id) === String(eventOrgId));
    if (!target) return;
    if (orgId[0] !== target.id) selectOrgId([target.id]);
    if (next === 'ticketing-dashboard' && location.state?.orgId !== target.id) {
      const eventId = typeof location.state === 'object' ? location.state?.eventId : location.state;
      navigate(location.pathname, { replace: true, state: { eventId, orgId: target.id } });
    }
  };

  const getOrganizations = async () => {
    let records = [];
    const q = query(
      collection(db, "Organizations"),
      where("adminUid", "array-contains", userStore.users?.uid),
    );
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((organizationDoc) => {
      const data = organizationDoc.data();
      records.push({ ...data, id: data.id || organizationDoc.id });
    });
    setOrganizations(records);

    let selectedOrg = [];
    for (const idx in records) {
      if (!selectedOrg.includes(records[idx].id)) {
        selectedOrg.push(records[idx].id);
      }
    }
    if (orgId.length === 0 && !isCreatingNewPos) {
      setOrgId([selectedOrg[0]]);
    }

    if (records.length === 0) {
      navigate("/dashboard/organizations");
    }

    setIsLoading(false);
  };

  const saveDashboardApps = async (dashboardApps) => {
    if (!userStore.users?.uid) return;

    setIsSavingDashboardApps(true);
    try {
      await setDoc(doc(db, "Store", userStore.users.uid), { dashboardApps }, { merge: true });
      dispatch(setUser({ ...userStore.users, dashboardApps }));
      setIsAppSelectorOpen(false);
    } catch (error) {
      console.error("Failed to save dashboard app preferences", error);
    } finally {
      setIsSavingDashboardApps(false);
    }
  };

  return (
    <div id="wrapper">
      {sidebarOpen && (
        <Sidebar
          organizations={organizations}
          setOrgId={selectOrgId}
          orgId={orgId}
          sidebarOpen={sidebarOpen}
          toggleSidebar={() => setSidebarOpen(false)}
          isCreatingNewPos={isCreatingNewPos}
          onOpenAppSelector={() => {
            setAppSelectorMode("settings");
            setIsAppSelectorOpen(true);
          }}
        />
      )}

      <div
        id="content-wrapper"
        className={`d-flex flex-column ${window.innerWidth <= 768 && sidebarOpen ? "blured" : ""}`}
        onClick={() => {
          if (window.innerWidth <= 768 && sidebarOpen) {
            setSidebarOpen(false);
          }
        }}
      >
        <div id="content">
          <Navbar
            setDateRange={setDateRange}
            startDate={startDate}
            endDate={endDate}
            setDateClickHandle={setDateClickHandle}
            dateClickHandle={dateClickHandle}
            setDateResetHandle={(value) => {
              if (next === "overview") {
                setDateRange(todayRange());
                setPaymentMethod("");
              }
              setDateResetHandle(value);
            }}
            dateResetHandle={dateResetHandle}
            setPaymentMethod={setPaymentMethod}
            paymentMethod={paymentMethod}
            comparisonEnabled={comparisonEnabled}
            setComparisonEnabled={setComparisonEnabled}
            sidebarOpen={sidebarOpen}
            toggleSidebar={() => setSidebarOpen(true)}
            organizations={organizations}
            orgId={orgId}
            setOrgId={selectOrgId}
            onCreateNewPos={next === "organizations" ? startNewPosCreation : undefined}
            isCreatingNewPos={isCreatingNewPos}
          />
          {next == "overview" && (
            <Dashboard
              organizations={organizations}
              setDateRange={setDateRange}
              orgId={orgId}
              dateClickHandle={dateClickHandle}
              startDate={startDate}
              endDate={endDate}
              dateResetHandle={dateResetHandle}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              comparisonEnabled={comparisonEnabled}
              comparisonConfiguration={comparisonConfiguration}
              setComparisonConfiguration={setComparisonConfiguration}
            />
          )}
          {next == "history" && (
            <History
              organizations={organizations}
              setDateRange={setDateRange}
              orgId={orgId}
              dateClickHandle={dateClickHandle}
              startDate={startDate}
              endDate={endDate}
              dateResetHandle={dateResetHandle}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
            />
          )}
          {next == "pos" && <POS organizations={organizations} orgId={orgId} />}
          {next == "organizations" && (
            <OrganizationsPage organizations={organizations} orgId={orgId} setOrgId={selectOrgId} />
          )}

          {next === "ticketing" && <TicketingOverview org={organization} />}
          {next === "ticketing-dashboard" && <TicketingDashboard org={organization} onOrganizationResolved={resolveTicketingOrganization} />}
          {next === "ticketing-create-edit" && <TicketingCreateEdit org={organization} />}

          {next === "stock-management" && <StockOverview org={organization} />}
          {next === "accreditations" && <Accreditations org={organization} />}
          {next === "budget" && <BudgetDashboard org={organization} />}
          {next === "notes" && <NotesPage org={organization} />}

          {next === "takeaway-overview" && <TakeawayOverview org={organization} />}
          {next === "takeaway-update-inscription" && <TakeawayUpdateInscription />}

          {next === "bartrack-overview" && <BartrackOverview key={orgId} org={organization} />}
          {next === "bartrack-line-details" && <BartrackLineDetails />}
          {next === "bartrack-setup" && <BartrackSetup org={organization} />}

          {next === "table-plan" && <TablePlan org={organization} />}
          {next === "cashless-cards" && (
            <CashlessPage organizations={organizations} orgId={orgId} />
          )}
          {next === "marketplace" && !providerId && ENV.ENABLE_MARKETPLACE && <Marketplace />}
          {next === "marketplace-cart" && ENV.ENABLE_MARKETPLACE && <MarketplaceCart />}
          {providerId && ENV.ENABLE_MARKETPLACE && <ProviderInfo />}
        </div>
      </div>

      {isLoading && <Loader loader={isLoading} />}
      <DashboardAppSelectorModal
        isOpen={isAppSelectorOpen}
        mode={appSelectorMode}
        initialSelectedAppIds={userStore.users?.dashboardApps?.selectedAppIds}
        initialProfile={userStore.users?.dashboardApps?.profile}
        isSaving={isSavingDashboardApps}
        onClose={() => setIsAppSelectorOpen(false)}
        onSave={saveDashboardApps}
      />
    </div>
  );
}

export default DashboardRouter;
